import React, { useEffect } from 'react';
import { VStack, HStack, Skeleton } from 'native-base';
import GCCard from '.';
import Barcode from '../Barcode';
import {
    GIFTCARD_FLOWS,
    GiftCard,
    observer,
    useUpdateGiftCard,
    useLocationListQuery,
    useAppSettings,
    useI18n,
    GiftCardLocationResponse,
    useSpringBigStore
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import { useToast } from '~/hooks/toast';
import { useAppNavigate } from '~/pages';
import { isAfter, addDays } from 'date-fns';
import { Button } from '~/shared/Theme';

type CradHolder = {
    cardContent: GiftCard;
    openNameModal: (value: boolean) => void;
    setOnSubmitAction: (value: GIFTCARD_FLOWS | undefined) => void
}

const CardHolder = ({ cardContent, openNameModal, setOnSubmitAction }: CradHolder) => {

    const { mutate: updateGiftCard, isLoading, error: requestGiftCardUpdateError } = useUpdateGiftCard();
    const [submitError, setSubmitError] = React.useState('');
    const { successToast } = useToast();
    const { data: locations } = useLocationListQuery();
    const { t } = useI18n();
    const { merchantOrg } = useAppSettings();
    const { navigate } = useAppNavigate();
    const { walletId, lastNameCheckDate, setLastNameCheckDate } = useSpringBigStore();
    const { member } = useSpringBigStore();

    let purchaseLocation: GiftCardLocationResponse[] | string | null = (locations && locations.length > 0 && locations?.filter(location => location.id.toString() === cardContent.purchase_location)) || null;
    purchaseLocation = purchaseLocation && purchaseLocation.length > 0 ? purchaseLocation[0].display_name : merchantOrg;

    const onManageFundsPress = () => {

        if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
            openNameModal(true);
            setOnSubmitAction(GIFTCARD_FLOWS.addFunds);
        }
        else {
            observer.emit('on-manage-cash', cardContent);
            observer.emit('open-actionsheet', GIFTCARD_FLOWS.addFunds);
        }
    };

    useEffect(() => {

        requestGiftCardUpdateError && setSubmitError(t('giftCard.update.error'));
    }, [requestGiftCardUpdateError]);

    const onMakePrimary = () => {

        updateGiftCard({
            id: cardContent.id,
            member_id: cardContent.member_id,
            auto_reload: cardContent.auto_reload,
            primary_card: true
        }, {
            onSuccess: (_) => {

                observer.emit('on-set-primary-gift-card');
                successToast(`${t('giftCard.update.success')} ${cardContent.card_number.slice(-5)}`);
            }
        });
    };

    const showTransactions = () => {

        if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
            if (!lastNameCheckDate || isAfter(
                new Date(),
                addDays(new Date(lastNameCheckDate), 4)
            )) {
                openNameModal(true);
                setLastNameCheckDate(new Date().toString());
                return;
            }
        }

        navigate(`w/${walletId}/giftCard/${cardContent.id}?page=1`, {
            state: {
                id: cardContent.id,
                cardNumber: cardContent.card_number
            },
        });

    };

    const CardRenderer = () => {

        return (
            <VStack>
                <GCCard card={cardContent} locations={locations} />
                <HStack justifyContent={'space-between'} alignItems={'baseline'}>
                    { isLoading ?
                        <Skeleton h={5} w={'6.3rem'} rounded={'lg'} />
                        :
                        cardContent.primary_card ?
                            <Caption
                                fontSize={16}
                                fontWeight={'300'}
                                text={'giftCard.primary.title'}
                            />
                            :
                            <Button
                                text={'giftCard.primary.button'}
                                fontSize={16}
                                px={0}
                                fontWeight={'300'}
                                variant={'text'}
                                disabled={isLoading || cardContent.primary_card || cardContent.is_closed_loop}
                                onPress={onMakePrimary}
                            />
                    }
                    <Button
                        variant={'text'}
                        text={'giftCard.transactions.view_transaction'}
                        fontSize={18}
                        onPress={showTransactions}
                    />
                </HStack>
                { submitError &&
                    <CaptionError>
                        { submitError }
                    </CaptionError>
                }
            </VStack>
        );
    };

    return (
        <VStack space={4}>
            <CardRenderer />
            <BarcodeRender
                cardContent={cardContent}
                onManageFundsPress={onManageFundsPress}
            />
        </VStack>
    );
};

const BarcodeRender = ({
    cardContent,
    onManageFundsPress,
}: {
    cardContent: GiftCard,
    onManageFundsPress: () => void,
}) => {

    const { merchant, giftCards } = useSpringBigStore();

    return (
        <VStack space={4}>
            <Barcode
                cardNumber={cardContent.card_number}
                cardPin={cardContent.card_pin}
            />
            {
                false &&
                merchant &&
                !merchant.hide_gift_card_payments &&
                ((cardContent.is_closed_loop && giftCards.length < 2 && cardContent.balance === '$0.00') || !cardContent.is_closed_loop) &&
                <Button
                    alignSelf={'center'}
                    fontWeight={'300'}
                    text={'giftCard.manage_funds'}
                    onPress={onManageFundsPress}
                />
            }
        </VStack>
    );
};

export default CardHolder;
